import type { ConfigProps } from '../../configTypes';
import { FeaturedImageFragment } from '../fragments/FeaturedImage';
import { ContributorsFragment } from '../fragments/Contributors';
import executeGraphQlRequest from './executeGraphQlRequest';
import { resolvePostImage } from '../lib/resolvePostImage';
import type { TransformedItemPost } from '../types';

type OriginalNode = {
  node: {
    id: string;
    databaseId: number;
    date: string;
    publishedDate?: string;
    modified?: string;
    customDateType?: string;
    excerpt: string;
    title: string;
    uri: string;
    slug: string;
    categories?: { nodes?: Array<{ name?: string | null } | null> | null };
    featuredImage?: {
      node: {
        altText: string;
        publicId: string;
        sourceUrl: string;
        mediaDetails?: { meta?: { credit?: string } };
      };
    };
    contributors?: { types?: unknown[] };
  };
};

export function embeddedPostsQueryAll(postTypeGql: string): string {
  return `
${FeaturedImageFragment(postTypeGql)}
${ContributorsFragment(postTypeGql)}
query EmbeddedPosts($ids: [ID!]!, $limit: Int!) {
  contentNodes(first: $limit, where: { in: $ids }) {
    edges {
      node {
        id
        ... on ${postTypeGql} {
          databaseId
          date: customDate
          publishedDate: date
          modified
          customDateType
          excerpt
          title
          uri
          slug
          categories(first: 1) {
            nodes {
              name
            }
          }
          ...FeaturedImageFragment
          ...ContributorsFragment
        }
        ... on Post {
          databaseId
          date: customDate
          publishedDate: date
          modified
          customDateType
          excerpt
          title
          uri
          slug
          categories(first: 1) {
            nodes {
              name
            }
          }
          featuredImage { node { publicId sourceUrl altText mediaDetails { meta { credit } } } }
          contributors { types { key value { title } } }
        }
        ... on Page {
          databaseId
          date: customDate
          publishedDate: date
          modified
          customDateType
          excerpt
          title
          uri
          slug
          categories(first: 1) {
            nodes {
              name
            }
          }
          featuredImage { node { publicId sourceUrl altText mediaDetails { meta { credit } } } }
          contributors { types { key value { title } } }
        }
      }
    }
  }
}
`;
}

export default async function fetchEmbeddedPosts(
  ids: string[],
  enrichedSiteSettings: ConfigProps,
): Promise<Record<string, TransformedItemPost> | null> {
  if (!ids.length) return null;
  const cmsUrl = enrichedSiteSettings.cmsUrl;
  const query = embeddedPostsQueryAll(enrichedSiteSettings.postTypeGql);
  const variables = { ids, limit: ids.length };

  try {
    const data = await executeGraphQlRequest<{
      contentNodes: { edges: OriginalNode[] };
    }>({ cmsUrl, query, variables });
    if (!data?.contentNodes?.edges?.length) return null;

    const transformedData = data.contentNodes.edges.map(
      (edge: OriginalNode) => {
        const publicId = edge.node.featuredImage?.node?.publicId;
        const srcUrl = edge.node.featuredImage?.node?.sourceUrl;
        const displayIso =
          edge.node.customDateType === 'show_published_date'
            ? edge.node.publishedDate || edge.node.date
            : edge.node.modified || edge.node.date;
        const date =
          displayIso && !Number.isNaN(Date.parse(displayIso))
            ? new Date(displayIso).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })
            : '';
        const types = edge.node.contributors?.types as
          | { key: string; value?: { title?: string }[] }[]
          | undefined;
        const contributorType =
          types?.find((t) => t.key === 'author' || t.key === 'writer') ||
          types?.[0];
        const contributorName = contributorType?.value?.[0]?.title;
        const categoryBrow =
          edge.node.categories?.nodes?.[0]?.name?.trim() || undefined;
        const item: TransformedItemPost = {
          id: String(edge.node.databaseId),
          title: edge.node.title || '',
          link: edge.node.uri || '',
          image:
            resolvePostImage(
              publicId ?? '',
              {},
              srcUrl,
              enrichedSiteSettings.fallbackPostImage,
            ) ?? undefined,
          imagealt:
            edge.node.featuredImage?.node?.altText || edge.node.title || '',
          content: edge.node.excerpt || '',
          date: date || '',
          brow: categoryBrow,
          contributors: contributorName
            ? [{ name: contributorName }]
            : undefined,
        };
        return item;
      },
    );

    const idToPost: Record<string, TransformedItemPost> = {};
    for (const item of transformedData) {
      idToPost[item.id] = item;
    }
    return idToPost;
  } catch {
    return null;
  }
}
